import { useCallback } from 'react';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import logExploreFilterSheetViewed from '@alltrails/analytics/events/logExploreFilterSheetViewed';
import SearchOrigin from '@alltrails/analytics/enums/SearchOrigin';
import AlgoliaSearchBox from 'components/AlgoliaSearchBox';
import FilterBar from 'components/explore/FilterBar';
import MobileExploreMapSwitch from 'components/MobileExploreMapSwitch';
import { SearchResultsCountArgs } from 'hooks/useHypotheticalSearchResultsCount';
import useUser from 'hooks/useUser';
import { Context } from '@alltrails/shared/types/context';
import { FilterRangeUpdates, Filters, FilterSliderUpdates } from 'types/Search';
import { AlgoliaResults } from 'types/Search/Algolia';
import { EXPLORE } from 'utils/constants/SearchAnalyticsConstants';
import { newFiltersReusingSearchState } from 'utils/filters';
import { legacyAlgoliaSearchAppQuery } from 'utils/legacyAlgoliaSearchQuery';
import ListMethods from 'types/ListMethods';
import NestedPanel from '../filters/NestedPanel';
import MobileFiltersModalButton from './MobileFiltersModalButton';
import getSearchBarFiltersConfigurations from './searchFiltersConfiguration';
import FiltersProvider from '../FiltersProvider';
import * as styles from './styles/styles.module.scss';

export type Props = {
  algoliaIndex: any;
  atMaps: any;
  clearAllFilters: () => void;
  clearRadioFilter?: (filterKey: string, pageName: string) => void;
  context: Context;
  defaultLatLng: any;
  enabledFilters: string[];
  enableSavedFilters?: boolean;
  filters: Filters;
  handleFilterRadioToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleFilterSet?: (filterKey: string, value: string) => void;
  handleFilterRange?: (filterKey: string, value: FilterRangeUpdates | FilterSliderUpdates) => void;
  handleFilterToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleObjectSelected: () => void;
  handleQueryTermSet: (queryString: string) => void;
  handleTrailSelected: () => void;
  initialUserSlug: string;
  isMobileWidth: boolean;
  listMethods: ListMethods;
  liveStringSearch: boolean;
  page: string;
  placeholderText: string;
  results: unknown[];
  resultsCount: number;
  typeSubsets: any;
};

const SearchFilterBar = ({
  algoliaIndex,
  atMaps,
  clearAllFilters,
  clearRadioFilter,
  context,
  defaultLatLng,
  enabledFilters,
  enableSavedFilters,
  filters,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterRange,
  handleFilterToggle,
  handleObjectSelected,
  handleQueryTermSet,
  handleTrailSelected,
  initialUserSlug,
  isMobileWidth,
  listMethods,
  liveStringSearch,
  page,
  placeholderText,
  results,
  resultsCount,
  typeSubsets
}: Props) => {
  const user = useUser();
  const intl = useIntl();

  const trackMobileFiltersClick = () => {
    const searchOrigin = filters.mobileMap ? SearchOrigin.ExploreMapView : SearchOrigin.ExploreListView;

    logExploreFilterSheetViewed({ search_origin: searchOrigin });
  };

  const sortedFilters = getSearchBarFiltersConfigurations(filters);

  // We have some overlapping work with the FilterBar here. The FilterBar expects
  // the entire sortedFilters array and has logic to reduce the filters and
  // display them where they belong. But the mobile web filters UI expects every
  // filter regardless of "nested" or "admin" status, so we have to filter out
  // inaccessible data here.
  // https://alltrails.atlassian.net/browse/DISCO-159
  const allFiltersForMobile = sortedFilters.filter(nextFilter => {
    if (!enabledFilters.includes(nextFilter.enabledKey)) {
      return false;
    }

    if (nextFilter.admin && (!context?.adminUser || !Object.keys(nextFilter.config).length)) {
      return false;
    }

    return true;
  });

  const searchResultsCountFunction = useCallback(
    ({ changes, insidePolygon, isochroneBoundingBox }: SearchResultsCountArgs): Promise<number> => {
      const filtersWithDraftChanges = {
        ...filters,
        ...changes
      };

      return new Promise(resolve => {
        legacyAlgoliaSearchAppQuery({
          algoliaIndex,
          atMaps,
          context,
          filters: filtersWithDraftChanges,
          // For /explore pages and typical trail searches this handler is called.
          handleSearchResults: (results: AlgoliaResults) => resolve(results?.hits?.length ?? 0),
          // If viewing the current user's custom maps this handler is called.
          // Note this isn't actually a search. This is a filter on atMaps.
          handleUserResults: (results: unknown[]) => resolve(results?.length ?? 0),
          initialUserSlug,
          isMobileWidth,
          insidePolygon,
          isochroneBoundingBox,
          listMethods,
          page
        });
      });
    },
    [algoliaIndex, atMaps, context, filters, initialUserSlug, isMobileWidth, listMethods, page]
  );

  return (
    <FiltersProvider
      blankFiltersFactory={() => {
        const { filters: blankFilters } = newFiltersReusingSearchState(atMaps, filters, results, {
          context,
          initialUserSlug,
          page
        });

        return getSearchBarFiltersConfigurations(blankFilters);
      }}
      clearAllFilters={clearAllFilters}
      clearRadioFilter={clearRadioFilter}
      handleFilterRadioToggle={handleFilterRadioToggle}
      handleFilterRange={handleFilterRange}
      handleFilterSet={handleFilterSet}
      handleFilterToggle={handleFilterToggle}
      page={page}
      resultsCount={resultsCount}
      searchResultsCountFunction={searchResultsCountFunction}
      sortedFilters={sortedFilters}
    >
      <div className={styles.container}>
        <div className={styles.searchBoxAndButtons}>
          <AlgoliaSearchBox
            className={styles.search}
            handleTrailSelect={handleTrailSelected}
            handleAreaSelect={handleObjectSelected}
            handleLocationSelect={handleObjectSelected}
            handleQueryStringSubmit={(queryString: string) => handleQueryTermSet(queryString.trim())}
            liveStringSearch={liveStringSearch}
            onSearchPage
            numResults={isMobileWidth ? 8 : 10}
            placeholderText={placeholderText}
            currentUser={context.currentUser}
            mobileBrowser={context.mobileBrowser}
            languageRegionCode={context.languageRegionCode}
            typeSubset={typeSubsets}
            defaultLatLng={defaultLatLng}
            listTrailIds={filters.trailIds}
            useNewSearchExperience
            page={EXPLORE}
            intl={intl}
          />

          <div className={styles.buttons}>
            <NestedPanel
              sortedFilters={allFiltersForMobile}
              title={<FormattedMessage defaultMessage="Filters" />}
              triggerElement={<MobileFiltersModalButton onClick={trackMobileFiltersClick} />}
              user={user}
            />
            <MobileExploreMapSwitch showMapMobile={filters.mobileMap} />
          </div>
        </div>

        <FilterBar context={context} enabledFilters={enabledFilters} enableSavedFilters={enableSavedFilters} />
      </div>
    </FiltersProvider>
  );
};

export default SearchFilterBar;
